import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ArrowLeft, FileText, Download, Archive, ShieldCheck, Clock, Check, Warning, CloudArrowDown, Gear, CalendarBlank, PaperPlaneTilt } from '@phosphor-icons/react';
import { toast } from 'sonner';
import SendSchedineModal from '../components/SendSchedineModal';
import AlloggiatiCheckinTab from '../components/AlloggiatiCheckinTab';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DatePicker({ value, onChange, placeholder = "Seleziona data", testId }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`w-full flex items-center gap-2 border border-zinc-300 px-3 py-2.5 text-left text-sm transition-colors hover:border-zinc-500 ${value ? 'text-zinc-900 font-medium' : 'text-zinc-400'}`}
          data-testid={testId}
        >
          <CalendarBlank size={16} weight="bold" className="text-zinc-400 flex-shrink-0" />
          {value ? format(value, 'd MMMM yyyy', { locale: it }) : placeholder}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 rounded-none border-zinc-300" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={(d) => { onChange(d); setOpen(false); }}
          locale={it}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export default function SchedinePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [downloads, setDownloads] = useState([]);
  const [archives, setArchives] = useState([]);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') || 'overview');
  const [archiveDate, setArchiveDate] = useState(null);

  // Config
  const [utente, setUtente] = useState('');
  const [password, setPassword] = useState('');
  const [wskey, setWskey] = useState('');
  const [strutture, setStrutture] = useState([]);
  const [selectedStruttura, setSelectedStruttura] = useState('');
  const [strutturaName, setStrutturaName] = useState('');
  const [configuring, setConfiguring] = useState(false);

  // Manual download
  const [dlDate, setDlDate] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Bulk download
  const [bulkFrom, setBulkFrom] = useState(null);
  const [bulkTo, setBulkTo] = useState(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [sRes, rRes, aRes] = await Promise.all([
        axios.get(`${API}/alloggiati/status`, { withCredentials: true }),
        axios.get(`${API}/alloggiati/ricevute`, { withCredentials: true }),
        axios.get(`${API}/alloggiati/archives`, { withCredentials: true }),
      ]);
      setStatus(sRes.data);
      setDownloads(rRes.data.downloads || []);
      setArchives(aRes.data);
      if (sRes.data.strutture) setStrutture(sRes.data.strutture);
      if (sRes.data.selected_struttura) setSelectedStruttura(sRes.data.selected_struttura);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleConfigure = async () => {
    if (!utente || !password || !wskey) { toast.error('Inserisci utente, password e WSKEY'); return; }
    setConfiguring(true);
    try {
      await axios.post(`${API}/alloggiati/token`, { utente, password, wskey }, { withCredentials: true });
      toast.success('Autenticazione riuscita! Schedine automatiche attivate.');
      setPassword('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Autenticazione fallita');
    }
    setConfiguring(false);
  };

  const handleAddStruttura = async () => {
    if (!strutturaName) { toast.error('Inserisci il nome della struttura'); return; }
    try {
      await axios.post(`${API}/alloggiati/strutture`, { name: strutturaName }, { withCredentials: true });
      toast.success('Struttura aggiunta!');
      setStrutturaName('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const handleSelectStruttura = async (id) => {
    try {
      await axios.post(`${API}/alloggiati/strutture/${id}/select`, {}, { withCredentials: true });
      setSelectedStruttura(id);
      toast.success('Struttura selezionata');
      fetchData();
    } catch (err) {
      toast.error('Errore selezione');
    }
  };

  const handleDownload = async () => {
    if (!dlDate) { toast.error('Seleziona una data'); return; }
    setDownloading(true);
    try {
      await axios.post(`${API}/alloggiati/download-single`, { date: format(dlDate, 'yyyy-MM-dd') }, { withCredentials: true });
      toast.success(`Ricevuta ${format(dlDate, 'd MMMM yyyy', { locale: it })} scaricata!`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Download fallito');
    }
    setDownloading(false);
  };

  const handleBulkDownload = async () => {
    if (!bulkFrom || !bulkTo) { toast.error('Seleziona il periodo'); return; }
    setBulkRunning(true);
    setBulkResult(null);
    try {
      const res = await axios.post(`${API}/alloggiati/download-all`, {
        date_from: format(bulkFrom, 'yyyy-MM-dd'), date_to: format(bulkTo, 'yyyy-MM-dd')
      }, { withCredentials: true });
      setBulkResult(res.data);
      toast.success(`Completato: ${res.data.downloaded} scaricate, ${res.data.skipped} già presenti, ${res.data.failed} fallite`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Download fallito');
    }
    setBulkRunning(false);
  };

  const downloadPdf = async (date) => {
    try {
      const res = await axios.get(`${API}/alloggiati/ricevute/${date}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `ricevuta_${date}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // The backend returns JSON errors; axios blob responses need manual parsing
      let msg = 'Errore nel download del PDF';
      try {
        if (err.response?.data instanceof Blob) {
          const txt = await err.response.data.text();
          const json = JSON.parse(txt);
          if (json?.detail) msg = json.detail;
        } else if (err.response?.data?.detail) {
          msg = err.response.data.detail;
        }
      } catch (_) { /* use default */ }
      toast.error(msg);
    }
  };

  const downloadArchive = async (filename) => {
    try {
      const res = await axios.get(`${API}/alloggiati/archives/${filename}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let msg = 'Errore nel download dell\'archivio';
      try {
        if (err.response?.data instanceof Blob) {
          const txt = await err.response.data.text();
          const json = JSON.parse(txt);
          if (json?.detail) msg = json.detail;
        } else if (err.response?.data?.detail) {
          msg = err.response.data.detail;
        }
      } catch (_) { /* use default */ }
      toast.error(msg);
    }
  };

  const generateMissingArchives = async () => {
    try {
      const res = await axios.post(`${API}/alloggiati/archives/generate-missing`, {}, { withCredentials: true });
      const count = res.data.count || 0;
      if (count === 0) {
        toast.info('Tutti gli archivi mensili sono già generati.');
      } else {
        toast.success(`${count} ${count === 1 ? 'archivio' : 'archivi'} mensili creati`);
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore generazione archivi');
    }
  };

  const handleCompress = async (year, month) => {
    try {
      await axios.post(`${API}/alloggiati/compress?year=${year}&month=${month}`, {}, { withCredentials: true });
      toast.success(`Archivio ${month}/${year} creato!`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Compressione fallita');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="schedine-page">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-4">
          <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-zinc-100 transition-colors" data-testid="back-button">
            <ArrowLeft size={24} weight="bold" className="text-zinc-700" />
          </button>
          <div>
            <h1 className="text-lg sm:text-xl font-black text-zinc-900 tracking-tight">Schedine Alloggiati Web</h1>
            <p className="text-xs text-zinc-400">Download automatico ricevute Polizia di Stato</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Status Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className={`p-4 border ${status?.configured ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
            <div className="flex items-center gap-2 mb-1">
              {status?.configured ? <Check size={16} weight="bold" className="text-emerald-600" /> : <Warning size={16} weight="bold" className="text-amber-600" />}
              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Stato</span>
            </div>
            <p className={`text-sm font-bold ${status?.configured ? 'text-emerald-700' : 'text-amber-700'}`}>{status?.configured ? 'Attivo' : 'Da configurare'}</p>
          </div>
          <div className="p-4 bg-white border border-zinc-200">
            <div className="flex items-center gap-2 mb-1"><CloudArrowDown size={16} weight="bold" className="text-blue-600" /><span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Scaricate</span></div>
            <p className="text-xl font-black text-zinc-900">{status?.total_downloads || 0}</p>
          </div>
          <div className="p-4 bg-white border border-zinc-200">
            <div className="flex items-center gap-2 mb-1"><FileText size={16} weight="bold" className="text-indigo-600" /><span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Ricevute</span></div>
            <p className="text-xl font-black text-zinc-900">{status?.total_ricevute || 0}</p>
          </div>
          <div className="p-4 bg-white border border-zinc-200">
            <div className="flex items-center gap-2 mb-1"><Archive size={16} weight="bold" className="text-teal-600" /><span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Archivi ZIP</span></div>
            <p className="text-xl font-black text-zinc-900">{archives.length}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-zinc-100 p-1 mb-6">
          {[
            { id: 'overview', label: 'Panoramica' },
            { id: 'checkin', label: 'Check-in' },
            { id: 'config', label: 'Configurazione' },
            { id: 'downloads', label: 'Ricevute' },
            { id: 'archives', label: 'Archivi' },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 py-2.5 text-xs sm:text-sm font-bold transition-all ${tab === t.id ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:text-zinc-700'}`} data-testid={`tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {tab === 'overview' && (
          <div className="space-y-4" data-testid="overview-tab">
            {/* Come funziona */}
            <div className="bg-white border border-zinc-200 p-5 sm:p-6">
              <h3 className="font-bold text-sm mb-3">Come funziona</h3>
              <div className="space-y-3">
                {[
                  { icon: Gear, title: 'Configura le credenziali', desc: 'Inserisci utente e password del portale Alloggiati Web della Polizia di Stato.' },
                  { icon: Clock, title: 'Download automatico alle 08:00', desc: 'Ogni mattina alle 08:00 il sistema scarica la ricevuta del giorno precedente e recupera in automatico le date mancanti degli ultimi 30 giorni.' },
                  { icon: Archive, title: 'Archiviazione mensile', desc: 'All\'inizio di ogni mese tutti i PDF del mese precedente vengono compressi in un archivio ZIP con notifica email.' },
                  { icon: ShieldCheck, title: 'Crittografia AES-256', desc: 'I PDF sono crittografati a riposo (Fernet/AES-256) e decriptati solo al momento del download.' },
                ].map((step, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-zinc-50">
                    <div className="w-8 h-8 bg-zinc-900 flex items-center justify-center flex-shrink-0">
                      <step.icon size={16} weight="bold" className="text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-zinc-900">{step.title}</p>
                      <p className="text-xs text-zinc-500">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dove trovare la WSKEY */}
            <div className="bg-indigo-50 border border-indigo-200 p-5 sm:p-6">
              <h3 className="font-bold text-sm text-indigo-900 mb-3">Dove trovo la WSKEY?</h3>
              <div className="space-y-2 text-xs text-indigo-800 leading-relaxed">
                <p>La <strong>WSKEY</strong> (Web Service Key) è la chiave che autorizza la comunicazione automatica con il portale della Polizia di Stato. Ecco come ottenerla:</p>
                <div className="bg-white border border-indigo-200 p-4 space-y-2">
                  <div className="flex items-start gap-2"><span className="w-5 h-5 bg-indigo-700 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">1</span><p>Accedi al portale <strong>alloggiatiweb.poliziadistato.it</strong> con le tue credenziali</p></div>
                  <div className="flex items-start gap-2"><span className="w-5 h-5 bg-indigo-700 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">2</span><p>Vai su <strong>Impostazioni</strong> (o <strong>Gestione Struttura</strong>)</p></div>
                  <div className="flex items-start gap-2"><span className="w-5 h-5 bg-indigo-700 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">3</span><p>Cerca la voce <strong>"Web Service"</strong> o <strong>"WSKEY"</strong></p></div>
                  <div className="flex items-start gap-2"><span className="w-5 h-5 bg-indigo-700 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">4</span><p>Se non presente, clicca su <strong>"Genera WSKEY"</strong> o <strong>"Richiedi abilitazione WS"</strong></p></div>
                  <div className="flex items-start gap-2"><span className="w-5 h-5 bg-indigo-700 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">5</span><p>Copia la chiave (formato base64, es. <code className="bg-indigo-100 px-1 py-0.5 text-[11px]">AMo9QBPD+Nuo...==</code>) e comunicala all'amministratore</p></div>
                </div>
                <p className="text-indigo-600 text-[11px] mt-2">Se non trovi l'opzione WSKEY, contatta la Questura di competenza per richiedere l'abilitazione ai Web Service.</p>
              </div>
            </div>

            {/* GDPR & Sicurezza */}
            <div className="bg-white border-2 border-emerald-200 p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck size={22} weight="bold" className="text-emerald-700" />
                <h3 className="font-bold text-sm text-emerald-900">GDPR e Sicurezza Dati</h3>
              </div>
              <p className="text-xs text-zinc-600 leading-relaxed mb-4">
                Le schedine contengono <strong>dati sensibili</strong> (documenti d'identità degli ospiti). Il sistema è progettato per garantire la massima conformità al GDPR e la sicurezza nel trattamento dei dati con la Questura.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                {[
                  { title: 'Crittografia a riposo', desc: 'Ogni PDF è crittografato con AES-256 (Fernet) prima del salvataggio. I file .pdf.enc non sono leggibili senza la chiave.', icon: '🔐' },
                  { title: 'Accesso autenticato', desc: 'Solo l\'utente autenticato (proprietario della struttura) può scaricare e visualizzare le proprie ricevute.', icon: '🔑' },
                  { title: 'Non accessibile pubblicamente', desc: 'I file sono archiviati in directory server non esposte. Nessun URL pubblico. Solo API autenticata.', icon: '🛡️' },
                  { title: 'Decriptazione on-demand', desc: 'I PDF vengono decriptati solo nel momento del download e trasmessi via HTTPS. Mai salvati in chiaro.', icon: '📄' },
                  { title: 'Canale sicuro con Questura', desc: 'La comunicazione con il portale Alloggiati avviene tramite SOAP/HTTPS con autenticazione WSKEY. Nessun dato transita in chiaro.', icon: '🏛️' },
                  { title: 'Log di accesso', desc: 'Ogni download viene registrato con timestamp e user_id per audit trail completo (GDPR Art. 30).', icon: '📋' },
                ].map((item, i) => (
                  <div key={i} className="bg-emerald-50 border border-emerald-100 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm">{item.icon}</span>
                      <p className="text-xs font-bold text-emerald-900">{item.title}</p>
                    </div>
                    <p className="text-[11px] text-zinc-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>

              {/* Normativa */}
              <div className="bg-zinc-50 border border-zinc-200 p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-2">Riferimenti normativi</p>
                <div className="space-y-1 text-[11px] text-zinc-600">
                  <p><strong>GDPR Art. 5</strong> — Minimizzazione dei dati: trattiamo solo le ricevute necessarie.</p>
                  <p><strong>GDPR Art. 25</strong> — Privacy by design: crittografia integrata nel flusso.</p>
                  <p><strong>GDPR Art. 32</strong> — Sicurezza: cifratura AES-256, accesso autenticato, HTTPS.</p>
                  <p><strong>GDPR Art. 30</strong> — Registro trattamenti: log di ogni operazione.</p>
                  <p><strong>D.Lgs. 196/2003</strong> — Codice Privacy: trattamento dati personali ospiti.</p>
                  <p><strong>Art. 109 TULPS</strong> — Obbligo comunicazione alloggiati alla P.S.</p>
                </div>
              </div>
            </div>

            {/* Informativa */}
            <div className="bg-amber-50 border border-amber-200 p-5 sm:p-6">
              <div className="flex items-start gap-3">
                <Warning size={20} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-amber-900 text-sm mb-2">Informativa Privacy — Trattamento Schedine</h4>
                  <div className="text-[11px] text-amber-800 leading-relaxed space-y-1.5">
                    <p>I dati personali degli ospiti (nome, cognome, documento d'identità, data di nascita, nazionalità) vengono trasmessi al Portale Alloggiati Web della Polizia di Stato in ottemperanza all'<strong>art. 109 del TULPS</strong> (Testo Unico Leggi di Pubblica Sicurezza).</p>
                    <p>Le ricevute PDF scaricate dal portale vengono <strong>crittografate con AES-256</strong> e archiviate in modo sicuro. L'accesso è riservato esclusivamente al titolare della struttura ricettiva.</p>
                    <p>I dati <strong>non vengono condivisi con terzi</strong>, non vengono utilizzati per finalità di marketing e non vengono trasferiti fuori dall'UE. La conservazione avviene per il tempo strettamente necessario agli obblighi di legge.</p>
                    <p>Titolare del trattamento: il gestore della struttura ricettiva. Responsabile del trattamento tecnico: Vita da Host (P.IVA 02903680995).</p>
                  </div>
                </div>
              </div>
            </div>

            {!status?.configured && (
              <div className="bg-amber-50 border-2 border-amber-300 p-5 flex items-start gap-3">
                <Warning size={24} weight="fill" className="text-amber-600 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-amber-900 text-sm">Configurazione richiesta</h3>
                  <p className="text-amber-800 text-xs mt-1">Vai nella tab "Configurazione" per inserire le credenziali del portale Alloggiati Web e attivare il download automatico.</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Check-in Tab (Premium add-on) */}
        {tab === 'checkin' && (
          <div data-testid="checkin-tab">
            <AlloggiatiCheckinTab />
          </div>
        )}

        {/* Config Tab */}
        {tab === 'config' && (
          <div className="space-y-4" data-testid="config-tab">
            {/* Credenziali */}
            <div className="bg-white border border-zinc-200 p-5 sm:p-6">
              <h3 className="font-bold text-sm mb-1">Credenziali Alloggiati Web</h3>
              <p className="text-xs text-zinc-400 mb-4">Servono Utente, Password e WSKEY del portale della Polizia di Stato.</p>

              {status?.configured ? (
                <div data-testid="alloggiati-connected-banner">
                  <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 text-white p-5 sm:p-6">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-10 h-10 bg-white/20 border border-white/30 flex items-center justify-center flex-shrink-0">
                        <ShieldCheck size={22} weight="fill" className="text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/80 mb-1">Connesso</p>
                        <h4 className="text-base sm:text-lg font-black tracking-tight">Alloggiati Web attivo</h4>
                        <p className="text-xs text-white/75 mt-1">
                          Il download automatico delle schedine è in funzione ogni mattina alle 08:00.
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-white/20">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-white/70">Ultimo accesso</p>
                        <p className="text-sm font-bold text-white mt-0.5">{status.last_auth?.slice(0, 10) || 'N/D'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-white/70">Schedine scaricate</p>
                        <p className="text-sm font-bold text-white mt-0.5">{status.total_downloads || 0}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSendModalOpen(true)}
                      className="mt-4 w-full bg-white text-emerald-800 hover:bg-emerald-50 px-4 py-3 text-sm font-black uppercase tracking-wider transition-colors flex items-center justify-center gap-2"
                      data-testid="open-send-schedine-btn"
                    >
                      <PaperPlaneTilt size={16} weight="bold" />
                      Invia schedine ora
                    </button>
                    <p className="mt-2 text-[10px] text-white/70 text-center leading-tight">
                      Inserisci i dati ospite e li invia direttamente alla Polizia di Stato (GDPR-compliant: cancellati dopo l'invio)
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      if (window.confirm('Vuoi modificare le credenziali Alloggiati Web? Dovrai inserirle nuovamente.')) {
                        setStatus({ ...status, configured: false });
                      }
                    }}
                    className="mt-3 text-xs text-zinc-500 hover:text-zinc-900 underline"
                    data-testid="change-credentials-btn"
                  >
                    Modifica credenziali
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-zinc-500 mb-1 block">Utente</Label>
                    <Input value={utente} onChange={e => setUtente(e.target.value)} placeholder="Username portale Alloggiati" className="rounded-none" data-testid="alloggiati-utente" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-500 mb-1 block">Password</Label>
                    <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password portale" className="rounded-none" data-testid="alloggiati-password" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-500 mb-1 block">WSKEY (Chiave Web Service)</Label>
                    <Input value={wskey} onChange={e => setWskey(e.target.value)} placeholder="Es. AMo9QBPD+NuopsDr3Qty..." className="rounded-none font-mono text-xs" data-testid="alloggiati-wskey" />
                    <p className="text-[10px] text-zinc-400 mt-1">Portale → Icona profilo → "Chiave Web Service" → "Genera Nuovo Codice". Si rigenera ad ogni cambio password.</p>
                  </div>
                  <Button onClick={handleConfigure} disabled={configuring} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold text-sm w-full py-3" data-testid="alloggiati-configure-btn">
                    {configuring ? 'Autenticazione in corso...' : 'Autentica e attiva download automatico'}
                  </Button>
                </div>
              )}
            </div>

            {/* Strutture multiple */}
            <div className="bg-white border border-zinc-200 p-5 sm:p-6">
              <h3 className="font-bold text-sm mb-1">Le tue strutture</h3>
              <p className="text-xs text-zinc-400 mb-4">Se hai più strutture nello stesso account (stessa Questura), aggiungile qui e seleziona da quale scaricare. Per strutture in Questure diverse serve un account separato con la sua WSKEY.</p>
              <div className="flex items-center gap-2 mb-4">
                <Input value={strutturaName} onChange={e => setStrutturaName(e.target.value)} placeholder="Nome struttura (es. Appartamento Via Roma)" className="rounded-none flex-1" data-testid="struttura-name-input" />
                <Button onClick={handleAddStruttura} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold text-xs px-4" data-testid="add-struttura-btn">Aggiungi</Button>
              </div>
              {strutture.length > 0 ? (
                <div className="space-y-2">
                  {strutture.map((s) => (
                    <div key={s.id} className={`flex items-center justify-between p-3 border cursor-pointer transition-all ${selectedStruttura === s.id ? 'border-indigo-500 bg-indigo-50' : 'border-zinc-200 hover:border-zinc-400'}`} onClick={() => handleSelectStruttura(s.id)} data-testid={`struttura-${s.id}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${selectedStruttura === s.id ? 'bg-indigo-600' : 'bg-zinc-300'}`} />
                        <span className="text-sm font-medium text-zinc-900">{s.name}</span>
                      </div>
                      {selectedStruttura === s.id && <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-600 bg-indigo-100 px-2 py-0.5">Attiva</span>}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-zinc-400 bg-zinc-50 p-3 text-center">Nessuna struttura aggiunta. Aggiungi la tua prima struttura per organizzare le schedine.</p>
              )}
            </div>

            <div className="bg-white border border-zinc-200 p-5 sm:p-6">
              <h3 className="font-bold text-sm mb-2">Download manuale — singola data</h3>
              <p className="text-xs text-zinc-400 mb-4">Scarica la ricevuta di un giorno specifico usando le credenziali configurate sopra.</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-zinc-500 mb-1 block">Data ricevuta</Label>
                  <DatePicker value={dlDate} onChange={setDlDate} placeholder="Seleziona giorno..." testId="dl-date-picker" />
                </div>
                <Button onClick={handleDownload} disabled={downloading || !status?.configured} variant="outline" className="rounded-none border-zinc-300 font-bold text-sm" data-testid="manual-download-btn">
                  <Download size={16} className="mr-2" /> {downloading ? 'Scaricamento...' : 'Scarica ricevuta'}
                </Button>
                {!status?.configured && <p className="text-[11px] text-amber-600">Configura prima le credenziali Alloggiati Web sopra.</p>}
              </div>
            </div>

            {/* BULK DOWNLOAD */}
            <div className="bg-indigo-50 border-2 border-indigo-300 p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-2">
                <CloudArrowDown size={22} weight="bold" className="text-indigo-700" />
                <h3 className="font-bold text-sm text-indigo-900">Scarica tutte le schedine — in tempo reale</h3>
              </div>
              <p className="text-xs text-indigo-700 mb-4 leading-relaxed">Scarica tutte le ricevute disponibili sul portale per il periodo selezionato. Usa le credenziali configurate sopra. Le schedine già scaricate verranno saltate automaticamente.</p>
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-indigo-600 mb-1 block font-bold">Da</Label>
                    <DatePicker value={bulkFrom} onChange={setBulkFrom} placeholder="Data inizio..." testId="bulk-from" />
                  </div>
                  <div>
                    <Label className="text-xs text-indigo-600 mb-1 block font-bold">A</Label>
                    <DatePicker value={bulkTo} onChange={setBulkTo} placeholder="Data fine..." testId="bulk-to" />
                  </div>
                </div>
                <Button onClick={handleBulkDownload} disabled={bulkRunning || !status?.configured} className="rounded-none bg-indigo-700 hover:bg-indigo-800 text-white font-bold text-sm w-full py-4" data-testid="bulk-download-btn">
                  <CloudArrowDown size={20} className="mr-2" />
                  {bulkRunning ? 'Download in corso... Attendere' : 'Scarica tutte le schedine del periodo'}
                </Button>
                {!status?.configured && <p className="text-[11px] text-indigo-500">Configura prima le credenziali nella sezione sopra per abilitare il download.</p>}
              </div>

              {bulkResult && (
                <div className="mt-4 bg-white border border-indigo-200 p-4 space-y-2">
                  <p className="text-xs font-bold text-indigo-900">Risultato download</p>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="bg-emerald-50 p-2 border border-emerald-200">
                      <p className="text-lg font-black text-emerald-700">{bulkResult.downloaded}</p>
                      <p className="text-[10px] text-zinc-500">Scaricate</p>
                    </div>
                    <div className="bg-zinc-50 p-2 border border-zinc-200">
                      <p className="text-lg font-black text-zinc-600">{bulkResult.skipped}</p>
                      <p className="text-[10px] text-zinc-500">Già presenti</p>
                    </div>
                    <div className="bg-red-50 p-2 border border-red-200">
                      <p className="text-lg font-black text-red-600">{bulkResult.failed}</p>
                      <p className="text-[10px] text-zinc-500">Non disponibili</p>
                    </div>
                  </div>
                  {bulkResult.details?.length > 0 && (
                    <div className="max-h-40 overflow-y-auto mt-2 space-y-1">
                      {bulkResult.details.map((d, i) => (
                        <div key={i} className={`text-[11px] flex items-center gap-2 px-2 py-1 ${d.status === 'ok' ? 'text-emerald-700 bg-emerald-50' : d.status === 'skipped' ? 'text-zinc-500 bg-zinc-50' : 'text-red-600 bg-red-50'}`}>
                          <span className="font-mono">{d.date}</span>
                          <span>{d.status === 'ok' ? `Scaricata (${(d.size/1024).toFixed(1)} KB)` : d.status === 'skipped' ? 'Già presente' : d.reason || 'Errore'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Downloads Tab */}
        {tab === 'downloads' && (
          <div data-testid="downloads-tab">
            {downloads.length === 0 ? (
              <div className="bg-white border border-zinc-200 p-8 text-center">
                <CloudArrowDown size={40} weight="thin" className="mx-auto text-zinc-300 mb-3" />
                <p className="text-zinc-400 text-sm">Nessuna ricevuta scaricata</p>
                <p className="text-zinc-300 text-xs mt-1">Le ricevute verranno scaricate automaticamente ogni notte</p>
              </div>
            ) : (
              <div className="space-y-2">
                {downloads.map((d, i) => (
                  <div key={i} className="bg-white border border-zinc-200 p-4 flex items-center justify-between group hover:border-zinc-400 transition-colors">
                    <div className="flex items-center gap-3">
                      <FileText size={20} weight="bold" className="text-indigo-600" />
                      <div>
                        <p className="text-sm font-bold text-zinc-900">Ricevuta {d.date}</p>
                        <p className="text-xs text-zinc-400">{d.auto ? 'Automatica' : 'Manuale'} · {((d.size || 0) / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                    <button onClick={() => downloadPdf(d.date)} className="p-2 hover:bg-zinc-100 transition-colors">
                      <Download size={18} className="text-zinc-500" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Archives Tab */}
        {tab === 'archives' && (
          <div data-testid="archives-tab">
            <div className="bg-white border border-zinc-200 p-5 sm:p-6 mb-4">
              <h3 className="font-bold text-sm mb-1">Crea archivio mensile</h3>
              <p className="text-xs text-zinc-400 mb-3">Seleziona un giorno qualsiasi del mese che vuoi comprimere.</p>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <DatePicker value={archiveDate} onChange={setArchiveDate} placeholder="Seleziona mese..." testId="archive-date-picker" />
                </div>
                <Button onClick={() => {
                  if (archiveDate) { handleCompress(archiveDate.getFullYear(), archiveDate.getMonth() + 1); }
                  else toast.error('Seleziona un mese');
                }} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold text-sm" data-testid="compress-btn">
                  <Archive size={16} className="mr-2" /> Comprimi
                </Button>
              </div>
              <p className="text-[10px] text-zinc-400 mt-2">Gli archivi ZIP vengono creati anche automaticamente all'inizio del mese successivo. Se qualcuno manca, usa il pulsante qui sotto.</p>
            </div>

            {/* Rigenera archivi mancanti */}
            <div className="bg-amber-50 border border-amber-200 p-4 mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-amber-900 mb-0.5">Archivi automatici</p>
                <p className="text-xs text-amber-800 leading-snug">Verifica se ci sono mesi chiusi senza archivio ZIP e li genera in automatico. Utile se il sistema era offline il 1° del mese.</p>
              </div>
              <Button onClick={generateMissingArchives} variant="outline" className="rounded-none border-amber-700 text-amber-900 hover:bg-amber-100 font-bold text-xs uppercase tracking-wider flex-shrink-0" data-testid="generate-missing-archives-btn">
                <Archive size={14} weight="bold" className="mr-1.5" /> Rigenera mancanti
              </Button>
            </div>
            {archives.length === 0 ? (
              <div className="bg-white border border-zinc-200 p-8 text-center">
                <Archive size={40} weight="thin" className="mx-auto text-zinc-300 mb-3" />
                <p className="text-zinc-400 text-sm">Nessun archivio creato</p>
              </div>
            ) : (
              <div className="space-y-2">
                {archives.map((a, i) => (
                  <div key={i} className="bg-white border border-zinc-200 p-4 flex items-center justify-between hover:border-zinc-400 transition-colors">
                    <div className="flex items-center gap-3">
                      <Archive size={20} weight="bold" className="text-teal-600" />
                      <div>
                        <p className="text-sm font-bold text-zinc-900">{a.filename}</p>
                        <p className="text-xs text-zinc-400">{(a.size / 1024).toFixed(1)} KB · {a.created?.slice(0, 10)}</p>
                      </div>
                    </div>
                    <button onClick={() => downloadArchive(a.filename)} className="p-2 hover:bg-zinc-100 transition-colors">
                      <Download size={18} className="text-zinc-500" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
      <SendSchedineModal open={sendModalOpen} onClose={() => setSendModalOpen(false)} />
    </div>
  );
}
